import { useParams, Link } from "react-router-dom";
import { ChevronLeftIcon, UsersIcon, PencilSquareIcon } from "@heroicons/react/24/outline";
import { useActivity } from "../hooks/useActivities";
import { useAuth } from "../hooks/useAuth";
import PageHeader from "../components/layout/PageHeader";
import { Card, CardHeader } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { sortAcs } from "../utils/acSort";

export default function ActivityDetail() {
  const { id } = useParams<{ id: string }>();
  const { activity, loading } = useActivity(Number(id));
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "director";

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-forest-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!activity) {
    return (
      <div className="p-8 text-center text-stone-500">
        Activity not found.{" "}
        <Link to="/activities" className="text-forest-600 underline">
          Back to activities
        </Link>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={activity.name}
        subtitle={activity.location}
        actions={
          <div className="flex gap-2 flex-wrap">
            {isAdmin && (
              <Link
                to={`/admin/activities?edit=${activity.id}`}
                className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 text-white font-semibold text-sm px-3 py-2 rounded-lg shadow-sm transition-colors backdrop-blur-sm"
              >
                <PencilSquareIcon className="w-4 h-4" />
                Edit Activity
              </Link>
            )}
            <Link to={`/activities/${activity.id}/progress`}>
              <Button variant="outline" icon={<UsersIcon className="w-4 h-4" />}>
                View Progress
              </Button>
            </Link>
          </div>
        }
      />

      <div className="p-6 lg:p-8">
        <Link
          to="/activities"
          className="inline-flex items-center gap-1 text-sm text-stone-500 hover:text-stone-700 mb-6 transition-colors"
        >
          <ChevronLeftIcon className="w-4 h-4" />
          All Activities
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Info */}
          <div className="lg:col-span-1 space-y-5">
            <Card>
              <CardHeader title="Details" />
              <dl className="space-y-3 text-sm">
                {activity.location && (
                  <div className="flex justify-between">
                    <dt className="text-stone-500">Location</dt>
                    <dd className="text-stone-700 font-medium">{activity.location}</dd>
                  </div>
                )}
              </dl>
            </Card>
          </div>

          {/* Achievement Credits */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader
                title="Achievement Credits"
                subtitle={`${activity.achievementCredits?.length ?? 0} credits for this activity`}
              />
              {(activity.achievementCredits ?? []).length === 0 ? (
                <p className="text-sm text-stone-400">No credits defined yet.</p>
              ) : (
                <div className="space-y-2">
                  {sortAcs(activity.achievementCredits ?? []).map((ac) => (
                    <div
                      key={ac.id}
                      className="flex items-start justify-between py-3 border-b border-stone-100 last:border-0"
                    >
                      <div>
                        <p className="text-sm font-medium text-stone-800">{ac.name}</p>
                        {ac.description && (
                          <p className="text-xs text-stone-400 mt-0.5">{ac.description}</p>
                        )}
                      </div>
                      <Badge label={String(ac.displayNumber)} color="stone" />
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
